<script setup>
import { ref, watch, computed } from 'vue';
import { t } from '../i18n';

const props = defineProps({
	modelValue: { type: Boolean, default: false },
	radiusMi: { type: Number, required: true },
	radiusMin: { type: Number, default: 5 },
	radiusMax: { type: Number, default: 500 },
});

const emit = defineEmits(['update:modelValue', 'apply', 'reset']);

const localRadius = ref(props.radiusMi);

watch(() => props.modelValue, (isOpen) => {
	if (isOpen) localRadius.value = props.radiusMi;
});

function close() { emit('update:modelValue', false); }

function apply() {
	const value = Math.min(props.radiusMax, Math.max(props.radiusMin, Number(localRadius.value) || props.radiusMi));
	emit('apply', { radiusMi: value });
	close();
}

function reset() {
	emit('reset');
	close();
}

const progress = computed(() => {
	return ((localRadius.value - props.radiusMin) / (props.radiusMax - props.radiusMin)) * 100;
});

const sliderStyle = computed(() => ({
	'--progress': `${progress.value}%`
}));
</script>

<template>
	<Teleport to="body">
		<Transition name="filters-popup">
			<div v-if="modelValue" class="filters-popup" @click.self="close">
				<div class="filters-popup__panel" role="dialog" aria-modal="true" :aria-label="t.filters">
					<header class="filters-popup__header">
						<h2 class="filters-popup__title">{{ t.filters }}</h2>
						<button type="button" class="filters-popup__close" :aria-label="t.closeFilters" @click="close">
							<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
								<path fill="#fff" d="m12 10.6 5-5 1.4 1.5-5 4.9 5 5-1.5 1.4-4.9-5-5 5L5.6 17l5-5-5-5L7 5.7z" />
							</svg>
						</button>
					</header>

					<div class="filters-popup__body">

						<div class="filters-popup__field">
							<label class="filters-popup__label" for="dl-filter-category">{{ t.category }}</label>
							<select id="dl-filter-category" disabled class="filters-popup__select">
								<option value="">{{ t.chooseCategory }}</option>
							</select>
						</div>

						<div class="filters-popup__field">
							<span class="filters-popup__label">{{ t.status }}</span>
							<label class="filters-popup__toggle">
								<input type="checkbox" disabled />
								<span class="filters-popup__toggle-track"><span class="filters-popup__toggle-thumb"></span></span>
								<span class="filters-popup__toggle-label">{{ t.open }}</span>
							</label>
						</div>

						<div class="filters-popup__field filters-popup__field--range">
							<span class="filters-popup__label">{{ t.distanceRange }}</span>
							<div class="filters-popup__range">
								<input type="range" class="filters-popup__slider" :min="radiusMin" :max="radiusMax" step="5"
									v-model.number="localRadius" :style="sliderStyle" />
								<input type="number" class="filters-popup__range-value" :min="radiusMin" :max="radiusMax"
									v-model.number="localRadius" />
							</div>
						</div>

					</div>

					<div class="filters-popup__actions">
						<button type="button" class="filters-popup__btn filters-popup__btn--apply" @click="apply">{{ t.apply }}</button>
						<button type="button" class="filters-popup__btn filters-popup__btn--reset" @click="reset">{{ t.resetFilters }}</button>
					</div>
				</div>
			</div>
		</Transition>
	</Teleport>
</template>
